import { FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { typeDNI, countries, countriesWithPhone } from './constants';
import { useEffect, useState, useMemo } from 'react';
import CustomTooltip from '@shared/components/Tooltip';
import StyledTextField from '@shared/components/StyledTextField';
import GenericDropdown from '@shared/components/GenericDropdown';
import FormSection from '@shared/components/FormSection';
import StyledFormControlRadioGroup from '@shared/components/StyledFormControlRadioGroup';

const FormPersonalData = ({
  index,
  passenger,
  passengerIndex,
  handleChangeAllCuit,
  allCuit,
  showSwitch,
  fullNameEqual,
  formik,
}) => {
  const [openTooltipName, setOpenTooltipName] = useState(-1);

  const pax = formik.values.passengers?.[index];

  let titlePassenger;
  if (passenger.passengerType === 0) {
    titlePassenger = `Adulto ${index === 0 ? '(Titular)' : ''}`;
  } else if (passenger.passengerType === 1) {
    titlePassenger = `Menor`;
  } else {
    titlePassenger = `Infante`;
  }

  useEffect(() => {
    if (fullNameEqual.includes(index)) {
      setOpenTooltipName(index);
    } else {
      setOpenTooltipName(-1);
    }
  }, [fullNameEqual]);

  useEffect(() => {
    if (!allCuit) return;

    const mainPassenger = formik.values.passengers[0];
    if (!mainPassenger) return;

    const updatedPassengers = formik.values.passengers.map((p, i) =>
      i === 0
        ? p
        : {
            ...p,
            cuitType: mainPassenger.cuitType,
            cuitNumber: mainPassenger.cuitNumber,
          }
    );

    formik.setFieldValue('passengers', updatedPassengers);
  }, [
    allCuit,
    formik.values.passengers?.[0]?.cuitType,
    formik.values.passengers?.[0]?.cuitNumber,
  ]);

  const selectedCountry = useMemo(() => {
    const countryValue = pax?.country;
    if (!countryValue) return null;
    const countryItem = countries.find(c => c.value === countryValue);
    if (!countryItem) return null;
    return {
      id: countryItem.value,
      value: countryItem.value,
      fragments: [{ text: countryItem.label, bold: false }],
    };
  }, [formik.values.passengers, index, countries]);

  const selectedCodePhone = useMemo(() => {
    const codeValue = pax?.codePhone;
    if (!codeValue) return null;
    const country = countriesWithPhone.find(c => c.value === codeValue);
    if (!country) return null;
    return {
      id: country.value,
      value: country.value,
      fragments: [{ text: country.label, bold: false }],
    };
  }, [formik.values.passengers, index, countriesWithPhone]);

  return (
    <FormSection
      title={titlePassenger}
      id={`passenger${passengerIndex}`}
      customLayout={{
        md: {
          1: 3,
          2: 3,
          3: 1.9,
          4: 1.6,
          5: 2.5,

          6: 3,
          7: 3,
          8: 1.9,
          9: 1.6,
          10: 2.5,

          11: 3,
          12: 3,
          13: 1.9,
        },
      }}
    >
      <Grid item xs={12} md={3}>
        <div style={{ position: 'relative' }}>
          <CustomTooltip
            anchor={openTooltipName}
            elementId={index}
            stylesProp={{
              top: '100%',
              left: 0,
              marginTop: '4px',
              border: '2px solid red',
              width: 'max-content',
              maxWidth: '300px',
              boxSizing: 'border-box',
            }}
          >
            <Typography style={{ color: 'red' }}>
              No podemos tomar dos personas con el mismo nombre. Por favor
              indica con JR o SR al final del mismo según correspondan las
              edades
            </Typography>
          </CustomTooltip>
          <StyledTextField
            formik={formik}
            name={`passengers[${index}].name`}
            label="Nombre (según documento)"
          />
        </div>
      </Grid>

      <StyledTextField
        formik={formik}
        name={`passengers[${index}].lastname`}
        label="Apellido (según documento)"
      />

      <StyledTextField
        formik={formik}
        name={`passengers[${index}].birth`}
        label="Fecha de Nacimiento"
        type="date"
        InputLabelProps={{ shrink: true }}
      />

      <GenericDropdown
        id={`passengers.${index}.typeDNI`}
        items={typeDNI.map((item, idx) => ({
          id: `${item.value}-${idx}`,
          value: item.value,
          fragments: [{ text: item.label, bold: false }],
        }))}
        selectedItem={
          pax?.typeDNI
            ? {
                id: formik.values.passengers[index].typeDNI,
                value: formik.values.passengers[index].typeDNI,
                fragments: typeDNI
                  .filter(
                    i => i.value === formik.values.passengers[index].typeDNI
                  )
                  .map(i => ({ text: i.label, bold: false })),
              }
            : null
        }
        onSelect={item =>
          formik.setFieldValue(`passengers[${index}].typeDNI`, item.value)
        }
        buttonLabel="Tipo Doc."
        hasError={
          formik.touched.passengers?.[index]?.typeDNI &&
          Boolean(formik.errors.passengers?.[index]?.typeDNI)
        }
        helperText={formik.errors.passengers?.[index]?.typeDNI}
      />

      <StyledTextField
        formik={formik}
        name={`passengers[${index}].dni`}
        type="text"
        label={pax?.typeDNI === 'DNI' ? 'Número de DNI' : 'Número de Pasaporte'}
        inputProps={{
          maxLength: pax?.typeDNI === 'DNI' ? 8 : 20,
          pattern: pax?.typeDNI === 'DNI' ? String.raw`\d*` : undefined,
        }}
        onChange={e => {
          let value = e.target.value;

          if (pax?.typeDNI === 'DNI') {
            value = value.replaceAll(/\D/g, '');
          }

          formik.setFieldValue(`passengers[${index}].dni`, value);
        }}
      />

      <StyledFormControlRadioGroup
        label="Género"
        name={`passengers[${index}].gender`}
        value={formik.values.passengers[index].gender || ''}
        onChange={newValue =>
          formik.setFieldValue(`passengers[${index}].gender`, newValue)
        }
        options={[
          { label: 'Masculino', value: 'masculino' },
          { label: 'Femenino', value: 'femenino' },
        ]}
        error={
          formik.touched.passengers?.[index]?.gender &&
          Boolean(formik.errors.passengers?.[index]?.gender)
        }
        helperText={formik.errors.passengers?.[index]?.gender}
        shape="square"
      />

      <GenericDropdown
        id={`passengers.${index}.country`}
        items={countries.map((c, idx) => ({
          id: `country-${c.value}-${idx}`,
          value: c.value,
          fragments: [{ text: c.label, bold: false }],
        }))}
        selectedItem={selectedCountry}
        onSelect={item =>
          formik.setFieldValue(`passengers[${index}].country`, item.value)
        }
        buttonLabel="País"
        hasError={
          formik.touched.passengers?.[index]?.country &&
          Boolean(formik.errors.passengers?.[index]?.country)
        }
        helperText={formik.errors.passengers?.[index]?.country}
      />

      <GenericDropdown
        id={`passengers.${index}.codePhone`}
        items={countriesWithPhone.map((country, idx) => ({
          id: `countryPhone-${country.value}-${idx}`,
          value: country.value,
          fragments: [{ text: country.label, bold: false }],
        }))}
        selectedItem={selectedCodePhone}
        onSelect={item =>
          formik.setFieldValue(`passengers[${index}].codePhone`, item.value)
        }
        buttonLabel="Código"
        hasError={
          formik.touched.passengers?.[index]?.codePhone &&
          Boolean(formik.errors.passengers?.[index]?.codePhone)
        }
        helperText={formik.errors.passengers?.[index]?.codePhone}
      />

      <StyledTextField
        formik={formik}
        name={`passengers[${index}].caracteristicPhone`}
        label="Cod. de área"
      />

      <StyledTextField
        formik={formik}
        name={`passengers[${index}].phone`}
        label="Celular (sin el 15)"
      />

      <StyledFormControlRadioGroup
        label="CUIL / CUIT pagador"
        error={
          formik.touched.passengers?.[index]?.cuitType &&
          Boolean(formik.errors.passengers?.[index]?.cuitType)
        }
        helperText={
          formik.touched.passengers?.[index]?.cuitType &&
          formik.errors.passengers?.[index]?.cuitType
            ? formik.errors.passengers[index].cuitType
            : ''
        }
        options={[
          { value: '2', label: 'CUIL' },
          { value: '1', label: 'CUIT' },
        ]}
        name={`passengers[${index}].cuitType`}
        value={pax?.cuitType || ''}
        onChange={newValue =>
          formik.setFieldValue(`passengers[${index}].cuitType`, newValue)
        }
        shape="square"
      />
      <StyledTextField
        formik={formik}
        name={`passengers[${index}].cuitNumber`}
        label={`Número de ${
          pax?.cuitType
            ? formik.values.passengers[index].cuitType === '1'
              ? 'CUIT'
              : 'CUIL'
            : 'CUIL / CUIT'
        }`}
      />

      {showSwitch && (
        <FormControlLabel
          sx={{
            height: '45px',
            fontSize: {
              xs: '14px',
              sm: '16px',
              md: '16px',
            },
            '& .MuiFormControlLabel-label': {
              borderRadius: '8px',
            },
          }}
          componentsProps={{
            typography: { sx: { color: '#000' } },
          }}
          control={
            <Switch
              checked={allCuit}
              onChange={() =>
                handleChangeAllCuit(pax?.cuitType, pax?.cuitNumber, index)
              }
              disabled={
                pax?.cuitNumber?.toString()?.length !== 11 || !pax?.cuitType
              }
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: theme => theme.tertiary,
                  '&.Mui-checked': {
                    color: theme => theme.tertiary,
                    '& + .MuiSwitch-track': {
                      backgroundColor: theme => theme.tertiary,
                    },
                  },
                },
                '& .MuiSwitch-track': {
                  backgroundColor: '#000',
                  opacity: 0.38,
                },
                '&:hover .MuiSwitch-switchBase': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
              }}
            />
          }
          label="Aplicar a todos"
        />
      )}
    </FormSection>
  );
};

export default FormPersonalData;
